import { DB_COLLECTIONS, DB_DEFAULTS } from "../../firebase/collections";
import firebase from "../../firebase/firebase";
import { mapEvent, mapMomentObjToRomeZoneDate, mapUserToVisitorSnippet } from "../../utils/mapping";
import { FUNCTIONS } from "../../firebase/functions";
import { Event, EventsByDay, EventSnippet, GetSignatureData, User } from "../../data/interfaces";
import moment from "moment";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------

// -----------------------------------------------------------------
// A c t i o n s
// -----------------------------------------------------------------
/**
 * Fetches all events by day
 * @function
 * @async
 *
 * @param day
 */
export const getEventsByDay = async (day: Date) => {
  try {
    // Load stands from firestore

    const normalizedDay = mapMomentObjToRomeZoneDate(moment(day), true);

    console.log("[Firebase read] events list of day", { day });
    const collectionRef = firebase
      .firestore()
      .collection(DB_COLLECTIONS.EVENTS)
      .where("day", "==", day)
      .where("public", "==", true)
      .orderBy("start_datetime", "asc");

    let querySnapshot = await collectionRef.get();

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: Event[] = [];

    querySnapshot.forEach((doc:any) => {
      data.push(mapEvent(doc.id, doc.data()));
    });

    console.log(data);

    return data;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Fetches events daily counters by month
 * @function
 * @async
 *
 * @param day
 */
export const getEventsCountersByMonth = async (day: Date) => {
  try {

    console.log("[Firebase read] events counters by day's month", { day });

    const startOfMonth = moment(day).startOf('month').toDate();
    const endOfMonth   = moment(day).endOf('month').toDate();

    const collectionRef = firebase
      .firestore()
      .collection(DB_COLLECTIONS.EVENTS_PER_DAY)
      .where("day", ">=", startOfMonth)
      .where("day", "<=", endOfMonth)

    let querySnapshot = await collectionRef.get();

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: EventsByDay[] = [];

    querySnapshot.forEach((doc: any) => {
      data.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return data;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Fetches events list filtered by stand.
 * @function
 * @async
 *
 * @param idStand
 * @param limit
 * @param last
 */
export const getStandEvents = async (idStand: string, limit?: number, last?: any) => {
  try {
    if (!idStand) {
      return [];
    }

    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    console.log("[Firebase] read, Get stand Events", idStand);
    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.EVENTS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef
        .where("stand.id", "==", idStand)
        .orderBy("start_datetime", "asc")
        .limit(limit)
        .get();
    } else {
      // With pagination
      querySnapshot = await collectionRef
        .where("stand.id", "==", idStand)
        .orderBy("start_datetime", "asc")
        .startAfter(last.start_datetime)
        .limit(limit)
        .get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach(function(doc) {
      data.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    return data;
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Fetches next events list in case there are none on the selected day by default
 * @function
 * @async
 *
 * @param day
 */
export const getNextEvents = async (day: moment.Moment) => {
  try {
    const normalizedDay = mapMomentObjToRomeZoneDate(day);

    console.log("[Firebase] read, Get next events", normalizedDay);

    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.EVENTS)
      .where("start_datetime", ">", normalizedDay)
      .where("public", "==", true)
      .orderBy("start_datetime", "asc")
      .limit(3);

    let querySnapshot = await collectionRef.get();

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach(function(doc) {
      data.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    return data;
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Fetches event detail.
 * @function
 * @async
 *
 * @param id
 */
export const getEventDetail = async (id: string) => {
  try {
    // Load events from firestore
    console.log("[Firebase] read event ", id);
    let querySnapshot = await firebase.firestore().collection(DB_COLLECTIONS.EVENTS).doc(id).get();

    return mapEvent(querySnapshot.id, querySnapshot.data());
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * Each request to Start or Join a Meeting / Webinar
 * must be verified by an encrypted signature
 * authorizing the user to enter.
 * @function
 * @async
 *
 * @param {GetSignatureData} data
 */
export const getSignature = async (data: GetSignatureData) => {
  console.log("getSignature", data);

  try {
    const getSignatureFunc = firebase.functions().httpsCallable(FUNCTIONS.ZOOM_GET_SIGNATURE);

    const result = await getSignatureFunc(data);

    return result.data.signature;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Tracks the participation to an event.
 * @function
 * @async
 *
 * @param user
 * @param event
 */
export const trackParticipantToEvent = async (user?: User | null, event?: EventSnippet) => {
  console.log("trackParticipantToEvent", user?.uid, event?.id);

  if (!user) {
    return;
  }

  if (!event) {
    return;
  }

  try {
    // Create a record with combined keys in contacts
    let combinedKey: string = `${user.uid}_${event.id}`;

    // Create record on firestore
    await firebase
      .firestore()
      .collection(DB_COLLECTIONS.EVENTS_PARTICIPANTS)
      .doc(combinedKey)
      .set(
        {
          date_joined: firebase.firestore.Timestamp.now(),
          user: mapUserToVisitorSnippet(user),
          event: event,
        },
        { merge: true },
      );
  } catch (error) {
    console.error(error);
    // throw new Error(error);
  }
};

/**
 * Fetches events list by user_id.
 * @function
 * @async
 *
 * @param uid
 * @param limit
 * @param last
 */
export const getUserEvents = async (uid: string, limit?: number, last?: any) => {
  try {
    if (!uid) {
      return [];
    }

    // Load stands from firestore
    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.EVENTS_PARTICIPANTS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef
        .where("user.uid", "==", uid)
        .orderBy("event.start_datetime", "desc")
        .limit(limit)
        .get();
    } else {
      // With pagination
      querySnapshot = await collectionRef
        .where("user.uid", "==", uid)
        .orderBy("event.start_datetime", "desc")
        .startAfter(last.event.start_datetime)
        .limit(limit)
        .get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach((doc:any) => {
      data.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return data;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Search events from firebase
 * @function
 * @async
 *
 * @param query
 * @param limit
 * @param last
 */
export const searchEvents = async (query: string, limit?: number, last?: any): Promise<any> => {
  try {
    if (!query || query.length < 3) {
      return [];
    }

    console.log("Search events: track ", query);

    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.EVENTS);
    let querySnapshot = null;

    // No pagination
    if (!last) {
      querySnapshot = await collectionRef
        .where("keywords", "array-contains", query.toLowerCase())
        .orderBy("start_datetime", "asc")
        .limit(limit)
        .get();
    } else {
      // With pagination
      querySnapshot = await collectionRef
        .where("keywords", "array-contains", query.toLowerCase())
        .orderBy("start_datetime", "asc")
        .startAfter(last.start_datetime)
        .limit(limit)
        .get();
    }

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: Event[] = [];

    querySnapshot.forEach((doc:any) => {
      data.push(mapEvent(doc.id, doc.data()));
    });

    return data;
  } catch (err) {
    console.error(err);
    throw new Error(err);
  }
};

/**
 * Search events from firebase by category
 * @function
 * @async
 *
 * @param category_id
 * @param limit
 * @param last
 */
export const getEventsByCategory = async (
  category_id: string,
  limit?: number,
  last?: any,
): Promise<any> => {
  return [];
  // try {
  //     console.log("Search events by category: ", category_id);

  //     if (!category_id) {
  //         return [];
  //     }

  //     if (!limit) {
  //         limit = DB_DEFAULTS.LIMIT;
  //     }

  //     const collectionRef = firebase.firestore().collection(DB_COLLECTIONS.EVENTS);
  //     let querySnapshot = null;

  //     // No pagination
  //     if (!last) {
  //         querySnapshot = await collectionRef
  //             .where("categories", "array-contains", category_id)
  //             .orderBy("start_datetime", "desc")
  //             .limit(limit)
  //             .get();
  //     } else {
  //         // With pagination
  //         querySnapshot = await collectionRef
  //             .where("categories", "array-contains", category_id)
  //             .orderBy("start_datetime", "desc")
  //             .startAfter(last.start_datetime)
  //             .limit(limit)
  //             .get();
  //     }

  //     // Impossible but in case...
  //     if (!querySnapshot) {
  //         return [];
  //     }

  //     let data: any[] = [];

  //     querySnapshot.forEach((doc:any) => {
  //         data.push(mapEvent(doc.id, doc.data()));
  //     });

  //     return data;
  // } catch (err) {
  //     console.error(err);
  //     throw new Error(err);
  // }
};

/**
 * Search related events by tags
 * @function
 * @async
 *
 * @param eventId
 * @param tags
 * @param limit
 * @param last
 */
export const getRelatedEventsByTags = async (
  eventId: string,
  tags: string[],
  limit?: number,
  last?: any,
): Promise<any> => {
  try {
    if (!eventId || !tags || !tags.length) {
      return [];
    }

    if (!limit) {
      limit = DB_DEFAULTS.LIMIT;
    }

    let collectionRef = firebase
      .firestore()
      .collection(DB_COLLECTIONS.EVENTS)
      .where("tags", "array-contains-any", tags)
      .where("public", "==", true)
      .orderBy("start_datetime", "desc");

    // No pagination
    if (!last) {
      collectionRef = collectionRef.limit(limit);
    } else {
      // With pagination
      collectionRef = collectionRef.startAfter(last.start_datetime).limit(limit);
    }

    let querySnapshot = await collectionRef.get();

    // Impossible but in case...
    if (!querySnapshot) {
      return [];
    }

    let data: any[] = [];

    querySnapshot.forEach((doc:any) => {
      if (doc.id !== eventId) {
        data.push(mapEvent(doc.id, doc.data()));
      }
    });

    console.log(data);

    return data;
  } catch (err) {
    console.error(err);
    throw err;
  }
};
